import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Header,
} from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Clock,
  Pause,
  Play,
  Copy,
  Check,
  ArrowRightLeft,
  Calendar,
  ChevronDown,
  ChevronUp,
  Globe,
  Table,
  FileSpreadsheet,
  Trash2,
  X,
} from 'lucide-react'

import {
  format,
  formatISO,
  getISOWeek,
  getQuarter,
  isLeapYear,
  isValid,
  parseISO,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

/* ── Types ── */
type TimeUnit = 'seconds' | 'milliseconds' | 'microseconds' | 'nanoseconds' | 'auto'
type Direction = 'timestamp-to-date' | 'date-to-timestamp'
type LiveUnit = 'seconds' | 'milliseconds' | 'microseconds' | 'nanoseconds'

interface BatchResult {
  input: string
  unit: string
  iso: string
  local: string
  seconds: string
  milliseconds: string
  error?: string
}

/* ── Constants ── */
const COMMON_TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'Local', value: 'local' },
  { label: 'GMT', value: 'GMT' },
  { label: 'EST (New York)', value: 'America/New_York' },
  { label: 'PST (Los Angeles)', value: 'America/Los_Angeles' },
  { label: 'CET (Berlin)', value: 'Europe/Berlin' },
  { label: 'JST (Tokyo)', value: 'Asia/Tokyo' },
  { label: 'CST (Shanghai)', value: 'Asia/Shanghai' },
  { label: 'IST (India)', value: 'Asia/Kolkata' },
  { label: 'AEDT (Sydney)', value: 'Australia/Sydney' },
  { label: 'BST (London)', value: 'Europe/London' },
]


function getLocalTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

/* ── Helper: detect unit from input ── */
function detectUnit(input: string): TimeUnit {
  const trimmed = input.trim()
  if (!trimmed || isNaN(Number(trimmed))) return 'auto'
  const absVal = Math.abs(Number(trimmed))
  const digits = trimmed.replace(/^-/, '').length

  if (digits === 10 && absVal >= 1e9 && absVal < 2e9) return 'seconds'
  if (digits === 13 && absVal >= 1e12 && absVal < 2e12) return 'milliseconds'
  if (digits === 16 && absVal >= 1e15 && absVal < 2e15) return 'microseconds'
  if (digits === 19 && absVal >= 1e18 && absVal < 2e18) return 'nanoseconds'

  // Fallback by magnitude
  if (absVal < 1e11) return 'seconds'
  if (absVal < 1e14) return 'milliseconds'
  if (absVal < 1e17) return 'microseconds'
  return 'nanoseconds'
}

/* ── Helper: normalize to milliseconds ── */
function toMilliseconds(value: number, unit: TimeUnit): number {
  switch (unit) {
    case 'seconds': return value * 1000
    case 'milliseconds': return value
    case 'microseconds': return Math.floor(value / 1000)
    case 'nanoseconds': return Math.floor(value / 1000000)
    default: return value
  }
}

/* ── Helper: format date in timezone ── */
function formatInTz(date: Date, tz: string, fmt: string): string {
  if (tz === 'local' || !tz) {
    return format(date, fmt)
  }
  try {
    return formatInTimeZone(date, tz, fmt)
  } catch {
    return format(date, fmt)
  }
}

/* ── Helper: copy to clipboard ── */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/* ── Helper: get DST status ── */
function isDST(date: Date, timeZone: string): boolean {
  try {
    const jan = new Date(date.getFullYear(), 0, 1)
    const jul = new Date(date.getFullYear(), 6, 1)
    const janOffset = new Date(jan.toLocaleString('en-US', { timeZone })).getTime() - jan.getTime()
    const julOffset = new Date(jul.toLocaleString('en-US', { timeZone })).getTime() - jul.getTime()
    const dateOffset = new Date(date.toLocaleString('en-US', { timeZone })).getTime() - date.getTime()
    return Math.max(janOffset, julOffset) !== dateOffset
  } catch {
    return false
  }
}

/* ── Helper: get UTC offset string ── */
function getUTCOffset(date: Date, timeZone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(date)
    const offsetPart = parts.find(p => p.type === 'timeZoneName')
    return offsetPart?.value || ''
  } catch {
    const offset = -date.getTimezoneOffset()
    const sign = offset >= 0 ? '+' : '-'
    const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0')
    const mins = (Math.abs(offset) % 60).toString().padStart(2, '0')
    return `GMT${sign}${hours}:${mins}`
  }
}

/* ── CopyButton component ── */
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const { t } = useI18n()

  const handleCopy = async () => {
    const ok = await copyToClipboard(text)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground"
      title={copied ? t('actions.copied') : t('actions.copy')}
    >
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      <span className="hidden sm:inline">{copied ? t('actions.copied') : (label || t('actions.copy'))}</span>
    </button>
  )
}

/* ── Main App ── */
function App() {
  const { t } = useI18n()

  /* State */
  const [direction, setDirection] = useState<Direction>('timestamp-to-date')
  const [inputValue, setInputValue] = useState('')
  const [selectedUnit, setSelectedUnit] = useState<TimeUnit>('auto')
  const [dateInput, setDateInput] = useState('')
  const [timeInput, setTimeInput] = useState('')
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const saved = localStorage.getItem('cikastudio-tz')
    return saved || getLocalTimezone()
  })
  const [isLive, setIsLive] = useState(true)
  const [liveUnit, setLiveUnit] = useState<LiveUnit>('seconds')
  const [liveTimestamp, setLiveTimestamp] = useState(Date.now())
  const [metadataExpanded, setMetadataExpanded] = useState(false)
  const [mode, setMode] = useState<'single' | 'batch'>('single')
  const [batchInput, setBatchInput] = useState('')
  const [batchResults, setBatchResults] = useState<BatchResult[]>([])
  const [error, setError] = useState('')

  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  /* Persist timezone */
  useEffect(() => {
    localStorage.setItem('cikastudio-tz', selectedTimezone)
  }, [selectedTimezone])

  /* Live mode interval */
  useEffect(() => {
    if (isLive) {
      liveIntervalRef.current = setInterval(() => {
        setLiveTimestamp(Date.now())
      }, 1000)
    }
    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current)
        liveIntervalRef.current = null
      }
    }
  }, [isLive])

  /* Auto-detect unit when input changes */
  const detectedUnit = useMemo(() => {
    if (selectedUnit !== 'auto') return selectedUnit
    return detectUnit(inputValue)
  }, [inputValue, selectedUnit])

  /* Convert timestamp → date */
  const convertedDate = useMemo(() => {
    if (!inputValue.trim() || isNaN(Number(inputValue))) return null
    const num = Number(inputValue)
    const unit = detectedUnit === 'auto' ? detectUnit(inputValue) : detectedUnit
    if (unit === 'auto') return null
    const ms = toMilliseconds(num, unit)
    const date = new Date(ms)
    if (!isValid(date) || ms < -8640000000000000 || ms > 8640000000000000) return null
    return date
  }, [inputValue, detectedUnit])

  /* Convert date → timestamp */
  const dateToTimestamp = useMemo(() => {
    if (!dateInput) return null
    const timeStr = timeInput || '00:00:00'
    const fullStr = `${dateInput}T${timeStr}`
    const parsed = parseISO(fullStr)
    if (!isValid(parsed)) return null
    return parsed.getTime()
  }, [dateInput, timeInput])

  /* Error handling */
  useEffect(() => {
    if (direction === 'timestamp-to-date') {
      if (inputValue && (isNaN(Number(inputValue)) || !inputValue.trim())) {
        setError(t('timestamp.error.invalidTimestamp'))
      } else if (inputValue && !convertedDate) {
        setError(t('timestamp.error.outOfRange'))
      } else {
        setError('')
      }
    } else {
      if (dateInput && dateToTimestamp === null) {
        setError(t('timestamp.error.invalidDate'))
      } else {
        setError('')
      }
    }
  }, [inputValue, convertedDate, dateInput, dateToTimestamp, direction, t])

  /* Output formats for timestamp→date */
  const outputFormats = useMemo(() => {
    if (!convertedDate) return null
    const tz = selectedTimezone === 'local' ? getLocalTimezone() : selectedTimezone
    return {
      iso8601: formatISO(convertedDate),
      rfc2822: formatInTz(convertedDate, tz, "EEE, dd MMM yyyy HH:mm:ss xxx"),
      sql: formatInTz(convertedDate, tz, 'yyyy-MM-dd HH:mm:ss'),
      unixSeconds: Math.floor(convertedDate.getTime() / 1000).toString(),
      unixMilliseconds: convertedDate.getTime().toString(),
      unixMicroseconds: (convertedDate.getTime() * 1000).toString(),
      unixNanoseconds: (convertedDate.getTime() * 1000000).toString(),
      humanReadable: formatInTz(convertedDate, tz, 'EEEE, MMMM do, yyyy \'at\' h:mm:ss a'),
    }
  }, [convertedDate, selectedTimezone])

  /* Output timestamps for date→timestamp */
  const outputTimestamps = useMemo(() => {
    if (dateToTimestamp === null) return null
    return {
      seconds: Math.floor(dateToTimestamp / 1000).toString(),
      milliseconds: dateToTimestamp.toString(),
      microseconds: (dateToTimestamp * 1000).toString(),
      nanoseconds: (dateToTimestamp * 1000000).toString(),
    }
  }, [dateToTimestamp])

  /* Metadata */
  const metadata = useMemo(() => {
    const date = direction === 'timestamp-to-date' ? convertedDate : (dateToTimestamp !== null ? new Date(dateToTimestamp) : null)
    if (!date || !isValid(date)) return null
    const tz = selectedTimezone === 'local' ? getLocalTimezone() : selectedTimezone
    const year = date.getFullYear()
    const dayOfYear = Math.floor((date.getTime() - new Date(year, 0, 0).getTime()) / 86400000)
    return {
      dayOfWeek: formatInTz(date, tz, 'EEEE'),
      dayOfYear: dayOfYear.toString(),
      weekNumber: getISOWeek(date).toString(),
      quarter: `Q${getQuarter(date)}`,
      leapYear: isLeapYear(year) ? t('timestamp.metadata.yes') : t('timestamp.metadata.no'),
      dst: isDST(date, tz) ? t('timestamp.metadata.active') : t('timestamp.metadata.inactive'),
      timezone: tz,
      utcOffset: getUTCOffset(date, tz),
    }
  }, [convertedDate, dateToTimestamp, direction, selectedTimezone, t])

  /* Live timestamp display */
  const liveDisplay = useMemo(() => {
    switch (liveUnit) {
      case 'seconds': return Math.floor(liveTimestamp / 1000).toString()
      case 'milliseconds': return liveTimestamp.toString()
      case 'microseconds': return (liveTimestamp * 1000).toString()
      case 'nanoseconds': return (liveTimestamp * 1000000).toString()
    }
  }, [liveTimestamp, liveUnit])

  /* Swap direction */
  const swapDirection = useCallback(() => {
    setDirection(prev => {
      const next = prev === 'timestamp-to-date' ? 'date-to-timestamp' : 'timestamp-to-date'
      if (next === 'date-to-timestamp' && convertedDate) {
        setDateInput(format(convertedDate, 'yyyy-MM-dd'))
        setTimeInput(format(convertedDate, 'HH:mm:ss'))
      } else if (next === 'timestamp-to-date' && dateToTimestamp !== null) {
        setInputValue(dateToTimestamp.toString())
        setSelectedUnit('milliseconds')
      }
      return next
    })
  }, [convertedDate, dateToTimestamp])

  /* Batch conversion */
  const runBatchConversion = useCallback(() => {
    const lines = batchInput.split('\n').filter(l => l.trim())
    const results: BatchResult[] = lines.map(line => {
      const trimmed = line.trim()
      const unit = detectUnit(trimmed)
      if (unit === 'auto' || isNaN(Number(trimmed))) {
        return { input: trimmed, unit: '-', iso: '-', local: '-', seconds: '-', milliseconds: '-', error: t('timestamp.error.invalidTimestamp') }
      }
      const num = Number(trimmed)
      const ms = toMilliseconds(num, unit)
      const date = new Date(ms)
      if (!isValid(date)) {
        return { input: trimmed, unit: unit === 'seconds' ? 's' : unit === 'milliseconds' ? 'ms' : unit === 'microseconds' ? 'μs' : 'ns', iso: '-', local: '-', seconds: '-', milliseconds: '-', error: t('timestamp.error.outOfRange') }
      }
      const tz = selectedTimezone === 'local' ? getLocalTimezone() : selectedTimezone
      return {
        input: trimmed,
        unit: unit === 'seconds' ? 's' : unit === 'milliseconds' ? 'ms' : unit === 'microseconds' ? 'μs' : 'ns',
        iso: formatISO(date),
        local: formatInTz(date, tz, 'yyyy-MM-dd HH:mm:ss'),
        seconds: Math.floor(date.getTime() / 1000).toString(),
        milliseconds: date.getTime().toString(),
      }
    })
    setBatchResults(results)
  }, [batchInput, selectedTimezone, t])

  /* Auto batch conversion on input change */
  useEffect(() => {
    if (mode !== 'batch') return
    const timer = setTimeout(() => {
      if (batchInput.trim()) {
        runBatchConversion()
      } else {
        setBatchResults([])
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [batchInput, mode, runBatchConversion])

  /* Download CSV */
  const downloadCSV = useCallback(() => {
    if (!batchResults.length) return
    const headers = [t('timestamp.batch.column.input'), t('timestamp.batch.column.unit'), t('timestamp.batch.column.iso'), t('timestamp.batch.column.local'), t('timestamp.batch.column.seconds'), t('timestamp.batch.column.milliseconds')]
    const rows = batchResults.map(r => [r.input, r.unit, r.iso, r.local, r.seconds, r.milliseconds])
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'timestamp-conversion.csv'
    link.click()
  }, [batchResults, t])

  /* Copy with feedback */
  const copyLiveTimestamp = useCallback(async (text: string) => {
    await copyToClipboard(text)
  }, [])

  /* Initialize date inputs for date→timestamp mode */
  useEffect(() => {
    if (!dateInput) {
      const now = new Date()
      setDateInput(format(now, 'yyyy-MM-dd'))
      setTimeInput(format(now, 'HH:mm:ss'))
    }
  }, [])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Main Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            {t('timestamp.title')}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('timestamp.subtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border">
              <Clock className="w-3 h-3" /> Live Mode
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border">
              <Table className="w-3 h-3" /> Batch Convert
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border">
              <Globe className="w-3 h-3" /> Privacy-First
            </span>
          </div>
        </div>

        {/* ── Live Timestamp Banner ── */}
        <div className="mb-6 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'}`} />
              <span className="font-semibold text-foreground">{t('timestamp.liveMode.title')}</span>
              <span className="text-xs text-muted-foreground">{t('timestamp.liveMode.description')}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsLive(!isLive)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground"
              >
                {isLive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {isLive ? t('timestamp.liveMode.pause') : t('timestamp.liveMode.resume')}
              </button>
              <select
                value={liveUnit}
                onChange={e => setLiveUnit(e.target.value as LiveUnit)}
                className="px-2 py-1.5 text-xs rounded-md border border-border bg-background text-foreground"
              >
                <option value="seconds">{t('timestamp.unit.seconds')}</option>
                <option value="milliseconds">{t('timestamp.unit.milliseconds')}</option>
                <option value="microseconds">{t('timestamp.unit.microseconds')}</option>
                <option value="nanoseconds">{t('timestamp.unit.nanoseconds')}</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => copyLiveTimestamp(liveDisplay)}
              className="text-4xl md:text-5xl font-mono text-foreground hover:text-primary transition-colors cursor-pointer select-all"
              title={t('timestamp.liveMode.copyHint')}
            >
              {liveDisplay}
            </button>
            <CopyButton text={liveDisplay} />
          </div>
        </div>

        {/* ── Mode Toggle (Single / Batch) ── */}
        <div className="flex items-center gap-1 mb-6 p-1 rounded-lg bg-muted w-fit">
          <button
            onClick={() => setMode('single')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${mode === 'single' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t('timestamp.batch.single')}
          </button>
          <button
            onClick={() => setMode('batch')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${mode === 'batch' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t('timestamp.batch.title')}
          </button>
        </div>

        {mode === 'single' ? (
          <>
            {/* ── Bidirectional Conversion Panel ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Left Panel - Input */}
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-foreground">
                    {direction === 'timestamp-to-date' ? t('timestamp.conversion.timestampToDate') : t('timestamp.conversion.dateToTimestamp')}
                  </h2>
                  <button
                    onClick={swapDirection}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground"
                    title={t('timestamp.conversion.swapDirection')}
                  >
                    <ArrowRightLeft className="w-3 h-3" />
                    <span className="hidden sm:inline">{t('timestamp.conversion.swapDirection')}</span>
                  </button>
                </div>

                {direction === 'timestamp-to-date' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">{t('timestamp.input.label')}</label>
                      <input
                        type="text"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        placeholder={t('timestamp.input.placeholder')}
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground">{t('timestamp.unit.auto')}:</label>
                      <select
                        value={selectedUnit}
                        onChange={e => setSelectedUnit(e.target.value as TimeUnit)}
                        className="px-2 py-1 text-sm rounded-md border border-border bg-background text-foreground"
                      >
                        <option value="auto">{t('timestamp.unit.auto')} ({detectedUnit !== 'auto' ? t(`timestamp.unit.${detectedUnit}`) : '...'})</option>
                        <option value="seconds">{t('timestamp.unit.seconds')}</option>
                        <option value="milliseconds">{t('timestamp.unit.milliseconds')}</option>
                        <option value="microseconds">{t('timestamp.unit.microseconds')}</option>
                        <option value="nanoseconds">{t('timestamp.unit.nanoseconds')}</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />{t('timestamp.input.label')}
                      </label>
                      <input
                        type="date"
                        value={dateInput}
                        onChange={e => setDateInput(e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">
                        <Clock className="w-3 h-3 inline mr-1" />Time
                      </label>
                      <input
                        type="time"
                        value={timeInput}
                        onChange={e => setTimeInput(e.target.value)}
                        step="1"
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mt-3 text-sm text-red-500 flex items-center gap-1">
                    <X className="w-4 h-4" />{error}
                  </div>
                )}
              </div>

              {/* Right Panel - Output */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h2 className="font-semibold text-foreground mb-4">{t('timestamp.output.label')}</h2>

                {direction === 'timestamp-to-date' && outputFormats ? (
                  <div className="space-y-3">
                    {[
                      { key: 'iso8601', label: t('timestamp.format.iso8601'), value: outputFormats.iso8601 },
                      { key: 'rfc2822', label: t('timestamp.format.rfc2822'), value: outputFormats.rfc2822 },
                      { key: 'sql', label: t('timestamp.format.sql'), value: outputFormats.sql },
                      { key: 'unixSeconds', label: t('timestamp.format.unixSeconds'), value: outputFormats.unixSeconds },
                      { key: 'unixMilliseconds', label: t('timestamp.format.unixMilliseconds'), value: outputFormats.unixMilliseconds },
                      { key: 'unixMicroseconds', label: t('timestamp.format.unixMicroseconds'), value: outputFormats.unixMicroseconds },
                      { key: 'unixNanoseconds', label: t('timestamp.format.unixNanoseconds'), value: outputFormats.unixNanoseconds },
                      { key: 'humanReadable', label: t('timestamp.format.humanReadable'), value: outputFormats.humanReadable },
                    ].map(({ key, label, value }) => (
                      <div key={key} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-muted-foreground">{label}</div>
                          <div className="text-sm font-mono text-foreground truncate">{value}</div>
                        </div>
                        <CopyButton text={value} />
                      </div>
                    ))}
                  </div>
                ) : direction === 'date-to-timestamp' && outputTimestamps ? (
                  <div className="space-y-3">
                    {[
                      { key: 'seconds', label: t('timestamp.format.unixSeconds'), value: outputTimestamps.seconds },
                      { key: 'milliseconds', label: t('timestamp.format.unixMilliseconds'), value: outputTimestamps.milliseconds },
                      { key: 'microseconds', label: t('timestamp.format.unixMicroseconds'), value: outputTimestamps.microseconds },
                      { key: 'nanoseconds', label: t('timestamp.format.unixNanoseconds'), value: outputTimestamps.nanoseconds },
                    ].map(({ key, label, value }) => (
                      <div key={key} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-muted-foreground">{label}</div>
                          <div className="text-sm font-mono text-foreground truncate">{value}</div>
                        </div>
                        <CopyButton text={value} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    {direction === 'timestamp-to-date'
                      ? t('timestamp.input.placeholder')
                      : t('timestamp.error.invalidDate')}
                  </div>
                )}
              </div>
            </div>

            {/* Timezone Selector */}
            <div className="mb-6 flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm text-muted-foreground">{t('timestamp.timezone.label')}:</label>
              <select
                value={selectedTimezone}
                onChange={e => setSelectedTimezone(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                ({getLocalTimezone()})
              </span>
            </div>

            {/* ── Metadata Panel ── */}
            {metadata && (
              <div className="rounded-xl border border-border bg-card overflow-hidden mb-6">
                <button
                  onClick={() => setMetadataExpanded(!metadataExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                >
                  <span className="font-semibold text-foreground">{t('timestamp.metadata.title')}</span>
                  {metadataExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                {metadataExpanded && (
                  <div className="p-4 pt-0 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: t('timestamp.metadata.dayOfWeek'), value: metadata.dayOfWeek },
                      { label: t('timestamp.metadata.dayOfYear'), value: metadata.dayOfYear },
                      { label: t('timestamp.metadata.weekNumber'), value: metadata.weekNumber },
                      { label: t('timestamp.metadata.quarter'), value: metadata.quarter },
                      { label: t('timestamp.metadata.leapYear'), value: metadata.leapYear },
                      { label: t('timestamp.metadata.dst'), value: metadata.dst },
                      { label: t('timestamp.metadata.timezone'), value: metadata.timezone },
                      { label: t('timestamp.metadata.utcOffset'), value: metadata.utcOffset },
                    ].map(({ label, value }) => (
                      <div key={label} className="p-3 rounded-md bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">{label}</div>
                        <div className="text-sm font-medium text-foreground">{value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* ── Batch Conversion Panel ── */
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">{t('timestamp.batch.title')}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setBatchInput(''); setBatchResults([]) }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground"
                >
                  <Trash2 className="w-3 h-3" />{t('timestamp.batch.clearAll')}
                </button>
                <button
                  onClick={downloadCSV}
                  disabled={!batchResults.length}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground disabled:opacity-50"
                >
                  <FileSpreadsheet className="w-3 h-3" />{t('timestamp.batch.downloadCSV')}
                </button>
              </div>
            </div>
            <textarea
              value={batchInput}
              onChange={e => setBatchInput(e.target.value)}
              placeholder={t('timestamp.batch.placeholder')}
              rows={6}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring mb-4 resize-y"
            />
            {batchResults.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.input')}</th>
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.unit')}</th>
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.iso')}</th>
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.local')}</th>
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.seconds')}</th>
                      <th className="text-left px-2 py-2 text-muted-foreground font-medium">{t('timestamp.batch.column.milliseconds')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResults.map((r, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="px-2 py-2 font-mono text-foreground">
                          <div className="flex items-center gap-1">
                            {r.error ? <X className="w-3 h-3 text-red-500 shrink-0" /> : <Check className="w-3 h-3 text-green-500 shrink-0" />}
                            <span className="truncate max-w-[120px]">{r.input}</span>
                          </div>
                          {r.error && <div className="text-xs text-red-500 mt-0.5">{r.error}</div>}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">{r.unit}</td>
                        <td className="px-2 py-2 font-mono text-foreground">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[180px]">{r.iso}</span>
                            {r.iso !== '-' && <CopyButton text={r.iso} />}
                          </div>
                        </td>
                        <td className="px-2 py-2 font-mono text-foreground">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[150px]">{r.local}</span>
                            {r.local !== '-' && <CopyButton text={r.local} />}
                          </div>
                        </td>
                        <td className="px-2 py-2 font-mono text-foreground">
                          <div className="flex items-center gap-1">
                            <span>{r.seconds}</span>
                            {r.seconds !== '-' && <CopyButton text={r.seconds} />}
                          </div>
                        </td>
                        <td className="px-2 py-2 font-mono text-foreground">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[140px]">{r.milliseconds}</span>
                            {r.milliseconds !== '-' && <CopyButton text={r.milliseconds} />}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
